import React from "react";
import { commitLocalUpdate, graphql, useFragment } from "react-relay";

import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { intervalToDuration } from "date-fns";
import type { Location } from "history";
import { throttle } from "lodash";
import { type Environment, fetchQuery } from "relay-runtime";
import invariant from "tiny-invariant";

import { BillingContext } from "~/account/Billing/BillingContext";
import growthbook from "~/lib/ab/growthbook";
import { useMedia } from "~/lib/hooks";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { capitalizeFirstLetter, copyToClipboard as utilsCopyToClipboard } from "~/lib/utils";
import WebsocketClient from "~/lib/websocket";
import { environment } from "~/models";
import toast from "~/ui/toast";

import type { ClipTabType } from "./ClipModal";
import type { TranscriptActionItem } from "./HighlightsPanel";

import type { RecapTemplateType } from "~/__generated__/HighlightsPanel_meetingRecording.graphql";
import type { utils_AddedOrAvailableUntil_meetingRecording$key } from "~/__generated__/utils_AddedOrAvailableUntil_meetingRecording.graphql";
import type { utils_getClipLinkQuery as GetClipURLQuery } from "~/__generated__/utils_getClipLinkQuery.graphql";
import type { utils_getRecordingLinkQuery as GetRecordingLinkQuery } from "~/__generated__/utils_getRecordingLinkQuery.graphql";
import type { utils_publicGetClipLinkQuery as PublicGetClipURLQuery } from "~/__generated__/utils_publicGetClipLinkQuery.graphql";
import type { utils_publicGetRecordingLinkQuery as PublicGetRecordingLinkQuery } from "~/__generated__/utils_publicGetRecordingLinkQuery.graphql";
import type { utils_useAutoRefetchMeetingRecording_note$key } from "~/__generated__/utils_useAutoRefetchMeetingRecording_note.graphql";
import type { utils_useGetURL_recording$key } from "~/__generated__/utils_useGetURL_recording.graphql";
import type { utils_useLongestMeetingRecording_meetingRecordings$key } from "~/__generated__/utils_useLongestMeetingRecording_meetingRecordings.graphql";
import type { utils_useMeetingRecorderStatus_note$key } from "~/__generated__/utils_useMeetingRecorderStatus_note.graphql";
import type { utils_useNoteHasRecordings_note$key } from "~/__generated__/utils_useNoteHasRecordings_note.graphql";
import type { MeetingRecordingStatus } from "~/__generated__/utilsMeetingRecorderStatusDummy.graphql";

type PublicContext =
	| { type: "recording"; accessKey: string; userExistsForEmail?: boolean }
	| { type: "clip"; clipId: string; userExistsForEmail?: boolean };
const PublicContext = React.createContext<PublicContext | null>(null);
PublicContext.displayName = "PublicContext";

export function PublicContextProvider({ children, ...value }: React.PropsWithChildren<PublicContext>) {
	return React.createElement(PublicContext.Provider, { value }, children);
}

export function useIsPublic(): boolean {
	return !!React.useContext(PublicContext);
}

export function usePublicAccessKey(): PublicContext | null {
	return React.useContext(PublicContext);
}

export function usePublicUserExistsForEmail(): boolean | undefined {
	const context = React.useContext(PublicContext);
	return context?.userExistsForEmail;
}

export function copyToClipboard(text: string | Promise<string>): Promise<void> {
	return utilsCopyToClipboard(text).then(() => void toast.success("Copied to clipboard"));
}

export function useIsNarrowViewport(): boolean {
	// @media (--max-md)
	return useMedia("(max-width: 734px)");
}

export function useIsSmallViewport(): boolean {
	// @media (--max-sm)
	return useMedia("(max-width: 479px)");
}

export function useIsReallyNarrowViewport(): boolean {
	// @media (--max-xs)
	return useMedia("(max-width: 319px)");
}

export function useLongestMeetingRecording<T>(
	meetingRecordingsKey: (utils_useLongestMeetingRecording_meetingRecordings$key & readonly T[]) | null,
): T | undefined {
	const meetingRecordings = useFragment<utils_useLongestMeetingRecording_meetingRecordings$key>(
		graphql`
			fragment utils_useLongestMeetingRecording_meetingRecordings on MeetingRecording @relay(plural: true) {
				meetingCallStart
				meetingCallEnd
				meetingRecordedVideoDuration
			}
		`,
		meetingRecordingsKey,
	);

	if (!meetingRecordings || !meetingRecordingsKey) return undefined;

	let longestMeetingRecordingIndex,
		longestDuration = 0;
	for (const [i, meetingRecording] of meetingRecordings.entries()) {
		let duration = 0;

		if (meetingRecording.meetingRecordedVideoDuration) {
			duration = meetingRecording.meetingRecordedVideoDuration * 1000;
		} else if (!meetingRecording.meetingCallStart || !meetingRecording.meetingCallEnd) {
			continue;
		} else {
			duration =
				new Date(meetingRecording.meetingCallEnd).getTime() - new Date(meetingRecording.meetingCallStart).getTime();
		}

		if (longestMeetingRecordingIndex === undefined || duration > longestDuration) {
			longestMeetingRecordingIndex = i;
			longestDuration = duration;
		}
	}

	if (longestMeetingRecordingIndex === undefined) return undefined;
	return meetingRecordingsKey[longestMeetingRecordingIndex];
}

type WsMessage =
	| { type: "MEETING_RECORDER_STATUS_UPDATED"; data: { meetingRecordingId: string; status: string } }
	| { type: "MEETING_ARTIFACTS_READY"; data: { meetingRecordingId: string } }
	| { type: "NOTE_UPDATED"; data: { finished: boolean } }
	| { type: "something else" };

// throttled so that when multiple components use the hook we don't make
// several requests when websocket messages come in
const refetchNote = throttle(function refetchNote(noteId: string) {
	fetchQuery(
		environment,
		graphql`
			query utils_refetchNote_Query($noteId: ID!) {
				node(id: $noteId) {
					...MeetingSummaryBanner_note
					...TranscriptModal_note @arguments(public: false)
					...MeetingRecordingBanner_RecordMeetingButton_note
					...MeetingPhaseContext_note
					...utils_useNoteHasRecordings_note
					# This fragment is here to prevent a crash when the note's event is
					# changed by someone else between when this client loaded the note
					# and when we refetch it here.
					...NoteCard_note

					... on Note {
						meetingRecordings {
							...utils_useLongestMeetingRecording_meetingRecordings
						}

						stream {
							# This field is used to decide whether the Ask Copilot sidebar
							# tab is visible.
							noteWithRecordingExists
						}
					}
				}

				viewer {
					remainingMeetingRecordingCredits
				}
			}
		`,
		{ noteId },
		{ fetchPolicy: "network-only" },
	).toPromise();
}, 100);

/**
 * Runs `callback` when a meeting recording for this note becomes available, or
 * when the meeting is marked finished.
 * @param noteKey - The note to watch. Can be null, but if it is, `callback` will
 * never be called.
 * @param callback - The function to call when the meeting recording becomes
 * available or the meeting is marked finished.
 * @param enabled - If false, `callback` will never be run. Use this when you
 * want to call this hook conditionally, but you can't, because it's a hook.
 */
export function useEffectOnMeetingRecordingFinished(
	noteKey: utils_useAutoRefetchMeetingRecording_note$key | null,
	callback: () => void,
	enabled = true,
): void {
	const note = useFragment(
		graphql`
			fragment utils_useAutoRefetchMeetingRecording_note on Note {
				id
				finished
				groupName

				meetingRecordings {
					id
					meetingCallStart
					status
				}
			}
		`,
		noteKey,
	);

	const latestMeetingRecording = React.useMemo(() => {
		let latest: NonNullable<NonNullable<typeof note>["meetingRecordings"]>[number] | undefined;
		for (const meetingRecording of note?.meetingRecordings ?? []) {
			if (
				!latest ||
				new Date(meetingRecording.meetingCallStart).getTime() > new Date(latest.meetingCallStart).getTime()
			) {
				latest = meetingRecording;
			}
		}
		return latest;
	}, [note?.meetingRecordings]);

	React.useEffect(() => {
		if (!note || !enabled) return;

		const socket = WebsocketClient.getInstance().getOrJoinGroup(note.groupName);

		function onSocketMessage(message: WsMessage) {
			if (
				"data" in message &&
				message.data &&
				"meetingRecordingId" in message.data &&
				message.data.meetingRecordingId &&
				message.data.meetingRecordingId !== latestMeetingRecording?.id
			) {
				callback();
				return;
			}

			if (message.type === "MEETING_ARTIFACTS_READY") {
				callback();
			} else if (message.type === "MEETING_RECORDER_STATUS_UPDATED") {
				callback();
			} else if (message.type === "NOTE_UPDATED") {
				if (note && message.data.finished !== note.finished) {
					callback();
				}
			}
		}

		socket.on("message", onSocketMessage);

		return () => {
			socket.off("message", onSocketMessage);
			return socket.close();
		};
	}, [callback, note, latestMeetingRecording?.id, enabled]);
}

/**
 * Automatically refetches the note when a meeting recording finishes.
 */
export function useAutoRefetchMeetingRecordings(
	note: (utils_useAutoRefetchMeetingRecording_note$key & { id: string }) | null,
): void {
	const refetch = React.useCallback(() => note && refetchNote(note.id), [note]);

	useEffectOnMeetingRecordingFinished(note, refetch);
}

graphql`
	fragment utilsMeetingRecorderStatusDummy on Note {
		meetingRecordings {
			status
		}
	}
`;

export function useMeetingRecorderStatus(noteKey: utils_useMeetingRecorderStatus_note$key | null): {
	status: MeetingRecordingStatus | null;
	recordingId: string | null;
} {
	const [status, setStatus] = React.useState<MeetingRecordingStatus | null>(null);
	const [recordingId, setRecordingId] = React.useState<string | null>(null);

	const note = useFragment(
		graphql`
			fragment utils_useMeetingRecorderStatus_note on Note {
				groupName
			}
		`,
		noteKey,
	);

	React.useEffect(() => {
		if (!note) return;

		const socket = WebsocketClient.getInstance().getOrJoinGroup(note.groupName);

		function onSocketMessage(message: WsMessage) {
			if (message.type === "MEETING_RECORDER_STATUS_UPDATED") {
				setStatus(message.data.status as MeetingRecordingStatus);
				setRecordingId(message.data.meetingRecordingId);
			}
		}

		socket.on("message", onSocketMessage);

		return () => {
			socket.off("message", onSocketMessage);
			return socket.close();
		};
	}, [note, recordingId]);

	return { status: status, recordingId: recordingId };
}

export function useNoteHasRecordings(noteKey: utils_useNoteHasRecordings_note$key): boolean {
	const note = useFragment(
		graphql`
			fragment utils_useNoteHasRecordings_note on Note {
				meetingRecordings {
					id
				}
			}
		`,
		noteKey,
	);
	return !!note.meetingRecordings && note.meetingRecordings.length !== 0;
}

const dateFormat = new Intl.DateTimeFormat("en-US", {
	day: "numeric",
	month: "short",
	year: "numeric",
});

export function formatDate(date: Date): string {
	return dateFormat.format(date);
}

export function useAddedOrAvailableUntil(meetingRecordingKey: utils_AddedOrAvailableUntil_meetingRecording$key | null) {
	const meetingRecording = useFragment(
		graphql`
			fragment utils_AddedOrAvailableUntil_meetingRecording on MeetingRecording {
				meetingCallEnd
				recordingAndTranscriptWillBeDeleted
				transcriptDeletionReason
				meetingRecordingVideoDeletionReason
				recordingDataAutoDeletionDate
			}
		`,
		meetingRecordingKey,
	);

	if (!meetingRecording) return null;

	const addedDate = meetingRecording.meetingCallEnd ? new Date(meetingRecording.meetingCallEnd) : null;

	const showDeletionDate =
		!meetingRecording.transcriptDeletionReason && !meetingRecording.meetingRecordingVideoDeletionReason;
	meetingRecording.recordingAndTranscriptWillBeDeleted && meetingRecording.recordingDataAutoDeletionDate;

	const deletionDate =
		showDeletionDate && meetingRecording.recordingDataAutoDeletionDate
			? new Date(meetingRecording.recordingDataAutoDeletionDate)
			: null;

	if (deletionDate) return `Available until ${formatDate(deletionDate)}`;
	else if (addedDate) return `Added ${formatDate(addedDate)}`;
	else return null;
}

export function formatDuration(duration: number) {
	const durationObj = intervalToDuration({
		start: 0,
		end: duration * 1000,
	});
	return `${durationObj.hours ? `${durationObj.hours}h ` : ""}${durationObj.minutes ? `${durationObj.minutes}m ` : ""}${
		durationObj.seconds ? `${durationObj.seconds}s` : ""
	}`;
}

export function timestampToHoursMinutesSeconds(timestamp: number) {
	const seconds = Math.floor(timestamp % 60),
		minutes = Math.floor((timestamp % 3600) - seconds) / 60,
		hours = Math.floor(timestamp / 3600);
	return { hours, minutes, seconds };
}

export function numHourDigitsForDuration(durationSeconds: number | null): number {
	if (durationSeconds === null) return 0;
	return durationSeconds < 3600 ? 0 : Math.floor(Math.log10(Math.floor(durationSeconds / 3600))) + 1;
}

export function formatTimestamp(timestamp: number, videoDuration?: number | null) {
	const { hours, minutes, seconds } = timestampToHoursMinutesSeconds(timestamp);

	const numHourDigits = numHourDigitsForDuration(
		videoDuration && Number.isFinite(videoDuration) ? videoDuration : timestamp,
	);

	const minutesAndSeconds = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
	if (numHourDigits === 0) return minutesAndSeconds;
	return `${hours.toString().padStart(numHourDigits, "0")}:${minutesAndSeconds}`;
}

const TIMESTAMP_REGEX = /^(?:(\d{1,2}):)?([0-5]\d):([0-5]\d)$/;

export function parseHumanReadableTimestamp(timestamp: string): number {
	const match = timestamp.trim().match(TIMESTAMP_REGEX);
	if (!match) throw new Error("Invalid timestamp format");

	const hours = match[1] !== undefined ? parseInt(match[1], 10) : 0;
	const minutes = parseInt(match[2], 10);
	const seconds = parseInt(match[3], 10);

	return hours * 3600 + minutes * 60 + seconds;
}

export function addTextTrackCuesToChapters<T>(
	textTrack: TextTrack,
	chapters: readonly T[],
	getTimestamp: (chapter: T) => number,
): (T & { cue: VTTCue | null })[] {
	// If a chapter has a timestamp value of -1, it means it's a topic without a timestamp
	// and we should skip adding cues for it
	function findCue(startTime: number): VTTCue | null {
		if (startTime === -1) return null;

		if (!textTrack.cues) {
			return null;
		}

		let earliestCue: VTTCue | null = null;
		for (let i = 0; i < textTrack.cues.length; i++) {
			const cue = textTrack.cues[i] as VTTCue;
			if (Math.abs(cue.startTime - startTime) < 0.5) {
				// Found a cue at this exact timestamp
				return cue;
			} else if (cue.startTime > startTime) {
				// Found a cue after this timestamp, see if it's the earliest one so far
				if (earliestCue === null || cue.startTime < earliestCue.startTime) {
					earliestCue = cue;
				}
			}
		}
		return earliestCue;
	}

	return chapters.map(chapter => ({
		...chapter,
		cue: findCue(getTimestamp(chapter)),
	}));
}

export function chaptersToText(
	chapters: readonly { title: string; timestamp?: number | null; bulletPoints: readonly BulletPoint[] }[],
	videoDuration: number | null,
): string {
	return chapters
		.map(chapter => {
			const chapterLines = [];

			// Add timestamp if it exists
			if (chapter.timestamp != null) {
				chapterLines.push(`[${formatTimestamp(chapter.timestamp, videoDuration)}] ${chapter.title}`);
			} else {
				// For topics without timestamps, just use the title
				chapterLines.push(chapter.title);
			}

			// Add bullet points
			chapterLines.push(chapterBulletsToText(chapter.bulletPoints, videoDuration));
			return chapterLines.join("\n");
		})
		.join("\n\n");
}

export function keyMomentsToText(keyMoments: readonly KeyMoment[], videoDuration: number | null): string {
	let keyMomentText = [];
	for (const keyMoment of keyMoments) {
		const timestampAndText = `[${formatTimestamp(keyMoment.timestamp, videoDuration)}] ${keyMoment.text}`;
		keyMomentText.push(timestampAndText);
	}
	return keyMomentText.join("\n");
}

export function actionItemToText(actionItem: TranscriptActionItem, videoDuration: number | null): string {
	const assignee = actionItem.assignees?.[0]?.fullName ? `${actionItem.assignees[0].fullName} - ` : "";
	return `[${formatTimestamp(actionItem.timestamp, videoDuration)}] ${assignee}${actionItem.text}`;
}

export function chapterBulletsToText(bulletPoints: readonly BulletPoint[], videoDuration: number | null): string {
	return bulletPoints
		.map(bulletPoint => {
			const bulletPointText = bulletPoint.text.trim();
			const timestamp = bulletPoint.timestamp ? `[${formatTimestamp(bulletPoint.timestamp, videoDuration)}] ` : "";
			return `- ${timestamp}${bulletPointText}`;
		})
		.join("\n");
}

export function emptyHighlightSectionText(videoDuration: number | null, sectionTypePlural: string): string {
	return videoDuration && videoDuration <= 5 * 60
		? `${capitalizeFirstLetter(sectionTypePlural)} are only generated for meeting recordings longer than 5 minutes. `
		: `No ${sectionTypePlural} were detected in this meeting. `;
}

const recordingLinkQuery = graphql`
	query utils_getRecordingLinkQuery($noteId: ID!, $recordingId: ID) {
		node(id: $noteId) {
			... on Note {
				id
				meetingRecordingLink {
					url(forCopy: true, recordingId: $recordingId)
				}
			}
		}
	}
`;

const publicRecordingLinkQuery = graphql`
	query utils_publicGetRecordingLinkQuery($accessKey: ID!, $recordingId: ID) {
		node(id: $accessKey) {
			... on Note {
				id
				meetingRecordingLink {
					url(forCopy: true, recordingId: $recordingId)
				}
			}
		}
	}
`;

function addTimestampToURL(url: URL, timestamp: number) {
	// The 0.01 increment is here to make sure that the resulting timestamp with
	// only two decimal places is actually bigger than the original timestamp.
	// This is important since activeTranscriptSegment will stop at the first
	// segment with a timestamp equal or greater than this truncated value.
	url.searchParams.set("ts", (timestamp + 0.01).toFixed(2));
}

const clipLinkQuery = graphql`
	query utils_getClipLinkQuery($clipId: ID!) {
		node(id: $clipId) {
			__typename
			... on MeetingRecordingClip {
				id
				url(forCopy: true)
			}
		}
	}
`;

// It's the same query but a different operation name so we can send it to the
// public endpoint instead of the graphql API
const publicClipLinkQuery = graphql`
	query utils_publicGetClipLinkQuery($clipId: ID!) {
		node(id: $clipId) {
			__typename
			... on MeetingRecordingClip {
				id
				url(forCopy: true)
			}
		}
	}
`;

export function useGetURL(recordingKey: utils_useGetURL_recording$key | null) {
	const recording = useFragment(
		graphql`
			fragment utils_useGetURL_recording on MeetingRecordingInterface {
				__typename
				... on MeetingRecording {
					id
				}
				... on MeetingRecordingClip {
					id
				}
			}
		`,
		recordingKey,
	);

	const publicAccessKey = usePublicAccessKey();

	return React.useMemo(() => {
		if (recording?.__typename === "MeetingRecording") {
			return async function getMeetingRecordingURL({
				timestamp,
				noteId,
			}: { timestamp?: number; noteId?: string } = {}) {
				let result;
				if (publicAccessKey) {
					invariant(publicAccessKey.type === "recording");
					result = await fetchQuery<PublicGetRecordingLinkQuery>(environment, publicRecordingLinkQuery, {
						accessKey: publicAccessKey.accessKey,
						recordingId: recording.id,
					}).toPromise();
				} else {
					invariant(noteId);
					result = await fetchQuery<GetRecordingLinkQuery>(environment, recordingLinkQuery, {
						noteId,
						recordingId: recording.id,
					}).toPromise();
				}
				if (!result?.node) throw new Error("Note not found");
				const { node: note } = result;
				if (!note.meetingRecordingLink) throw new Error("Meeting recording link not found");

				const url = new URL(note.meetingRecordingLink.url, window.location.origin);
				if (timestamp !== undefined) addTimestampToURL(url, timestamp);

				return url.toString();
			};
		} else if (recording?.__typename === "MeetingRecordingClip") {
			return async function getClipURL({ timestamp }: { timestamp?: number } = {}) {
				let result: GetClipURLQuery["response"] | PublicGetClipURLQuery["response"] | undefined;
				if (publicAccessKey) {
					result = await fetchQuery<PublicGetClipURLQuery>(environment, publicClipLinkQuery, {
						clipId: recording.id,
					}).toPromise();
				} else {
					result = await fetchQuery<GetClipURLQuery>(environment, clipLinkQuery, {
						clipId: recording.id,
					}).toPromise();
				}

				if (!result?.node) throw new Error("Clip not found");
				const { node: clip } = result;
				if (clip.__typename !== "MeetingRecordingClip") throw new Error("Clip not found");

				const url = new URL(clip.url, window.location.origin);
				if (timestamp !== undefined) addTimestampToURL(url, timestamp);

				return url.toString();
			};
		} else {
			return undefined;
		}
	}, [recording, publicAccessKey]);
}

export function getThumbnailImageCoords(urlString: string): [x: number, y: number, w: number, h: number] | undefined {
	const url = new URL(urlString);
	const coords = new URLSearchParams(url.hash).get("#xywh");
	if (!coords) return undefined;
	const [x, y, w, h] = coords.split(",").map(n => parseInt(n, 10));
	return [x, y, w, h];
}

export enum HighlightFeedbackSource {
	SUMMARY = "SUMMARY",
	CHAPTERS = "CHAPTERS",
	DECISIONS = "DECISIONS",
	ACTION_ITEMS = "ACTION_ITEMS",
}

export type RecapTemplate = {
	[key: string]: KeyMoment[];
};

export type KeyMoment = {
	timestamp: number;
	text: string;
};

export type BulletPoint = {
	text: string;
	timestamp: number | null;
	textShort: string | null;
	context: string | null;
};

export const TEMPLATE_TITLES: Record<RecapTemplateType, string> = {
	SALES_DISCOVERY: "Sales Discovery",
	SALES_FOLLOW_UP: "Sales Follow Up",
	BANT: "BANT",
	MEDDIC: "MEDDIC",
	SPICED: "SPICED",
	CUSTOMER_SUCCESS: "Customer Success",
};

export const TEMPLATE_OPTIONS = [
	{
		label: "General meeting",
		value: "GENERAL",
	},
	{
		label: "Sales Discovery",
		value: "SALES_DISCOVERY",
	},
	{
		label: "Sales Follow Up",
		value: "SALES_FOLLOW_UP",
	},
	{
		label: "BANT",
		value: "BANT",
	},
	{
		label: "MEDDIC",
		value: "MEDDIC",
	},
	{
		label: "SPICED",
		value: "SPICED",
	},
	{
		label: "Customer Success",
		value: "CUSTOMER_SUCCESS",
	},
] as const;

export type TemplateOption = (typeof TEMPLATE_OPTIONS)[number];

export function useAnalyticsTrack() {
	const { analyticsFingerprint: fingerprint } = React.useContext(BillingContext);

	return React.useCallback(
		(event: string, properties?: object) => {
			dispatchAnalyticsTrack(event, { fingerprint, ...properties });
		},
		[fingerprint],
	);
}

export function getTimestampFromLocation(location: Location, paramName = "ts") {
	let timestampString = new URLSearchParams(location.search).get(paramName);

	if (timestampString == null) {
		timestampString = new URLSearchParams(location.hash).get(paramName);
	}

	if (timestampString != null) {
		const timestamp = parseFloat(timestampString);
		if (!isNaN(timestamp)) return timestamp;
	}

	return undefined;
}

export const getClipTabFromLocation = (location: Location, paramName = "tab"): ClipTabType | undefined => {
	const tab = new URLSearchParams(location.search).get(paramName);
	return tab as ClipTabType | undefined;
};

const WATCH_STATS_FEATURE_FLAG = "watch-stats";
const WATCH_STATS_SHOW_PERCENTAGE_FEATURE_FLAG = "watch-stats-show-percentage";
const FIRST_VIEW_NOTIFICATION_FEATURE_FLAG = "first-view-notification";

export function useWatchStatsFeatureFlagOn(): boolean {
	return useFeatureIsOn(WATCH_STATS_FEATURE_FLAG);
}

export function useWatchStatsShowPercentageFeatureFlagOn(): boolean {
	const watchStatsFeatureFlagOn = useWatchStatsFeatureFlagOn();
	const watchStatsShowPercentageFeatureFlagOn = useFeatureIsOn(WATCH_STATS_SHOW_PERCENTAGE_FEATURE_FLAG);
	return watchStatsFeatureFlagOn && watchStatsShowPercentageFeatureFlagOn;
}

export function useFirstViewNotificationFeatureFlagOn(): boolean {
	const firstViewNotificationFeatureFlagOn = useFeatureIsOn(FIRST_VIEW_NOTIFICATION_FEATURE_FLAG);
	const watchStatsFeatureFlagOn = useWatchStatsFeatureFlagOn();
	return firstViewNotificationFeatureFlagOn && watchStatsFeatureFlagOn;
}

export const REAL_AI_ACTION_ITEMS_FEATURE_FLAG = "real-ai-action-items";
export function useRealAiActionItemsIsOn(): boolean {
	return useFeatureIsOn(REAL_AI_ACTION_ITEMS_FEATURE_FLAG);
}

export const AI_ACTION_ITEMS_USER_SETTING_AND_ACCEPT_FLOW_FEATURE_FLAG = "ai-action-items-user-setting-and-accept-flow";
/**
This flag controls 3 features for AI Action Items:
- The ability to accept/reject AI action items
- The visibility of the user setting to auto-accept AI action items
- The AI Suggestions section in the Action Items page
*/
export function useAiActionItemsUserSettingAndAcceptFlowIsOn(): boolean {
	return useFeatureIsOn(AI_ACTION_ITEMS_USER_SETTING_AND_ACCEPT_FLOW_FEATURE_FLAG);
}

export function usePublicRecapLockVideoPlayback() {
	const isPublic = useIsPublic();
	// Using the useFeatureIsOn hook here would require it being called before isPublic which would
	// result in over exposing the experiment. For that reason I intentionally use growthbook.isOn
	return isPublic && growthbook.isOn("public-recap-lock-video-playback");
}

interface CommentChangePayload {
	data: {
		threadId: string;
		startTimestamp: string;
		endTimestamp: string;
		count: number;
		users: Array<{ id: string; email: string; fullName: string; image: string }>;
		targetUsers: Array<{ id: string; fullName: string }>;
		commentOperation: string;
	};
}

/**
 * Processes websocket comment updates for meeting recordings.
 *
 * Handles comment operations (resolved, thread_deleted, updates, new comments) by directly
 * updating the Relay store. Manages existing comment threads and creates new ones as needed.
 *
 * @param environment - Relay environment for store updates
 * @param payload - Websocket payload containing comment change data
 * @param recordingId - ID of the meeting recording to update
 * @param userId - Current user's ID for filtering target users
 */
export function processRecordingCommentUpdates(
	environment: Environment,
	payload: CommentChangePayload,
	recordingId: string,
	userId?: string,
): void {
	if (!recordingId) return;

	commitLocalUpdate(environment, store => {
		const meetingRecordingRecord = store.get(recordingId);
		if (!meetingRecordingRecord) return;

		const comments = meetingRecordingRecord.getLinkedRecords("comments") || [];

		// Handle comment deletion (resolved or thread deleted)
		if (payload.data.commentOperation === "resolved" || payload.data.commentOperation === "thread_deleted") {
			const updatedComments = comments.filter(comment => {
				return comment.getValue("threadId") !== payload.data.threadId;
			});
			meetingRecordingRecord.setLinkedRecords(updatedComments, "comments");
			return;
		}

		// Handle comment updates and new comments
		let threadAlreadyExists = false;
		const updatedComments = comments.map(comment => {
			if (comment.getValue("threadId") === payload.data.threadId) {
				comment.setValue(payload.data.count, "count");
				comment.setLinkedRecords(
					payload.data.users.map(user => {
						let userRecord = store.get(user.id);
						if (userRecord) {
							return userRecord;
						}

						userRecord = store.create(user.id, "User");
						userRecord.setValue(user.id, "id");
						userRecord.setValue(user.email, "email");
						userRecord.setValue(user.fullName, "fullName");
						userRecord.setValue(user.image, "image");
						return userRecord;
					}),
					"users",
				);
				threadAlreadyExists = true;
			}
			return comment;
		});

		// Create new comment thread if it doesn't exist
		if (!threadAlreadyExists) {
			const { targetUsers = [] } = payload.data;
			if (targetUsers.length > 0 && !targetUsers.find(u => userId === u.id)) return;

			const newCommentId = `comment-${Date.now()}`;
			const newComment = store.create(newCommentId, "Comment");
			newComment.setValue(payload.data.threadId, "threadId");
			newComment.setValue(payload.data.startTimestamp, "startTimestamp");
			newComment.setValue(payload.data.endTimestamp, "endTimestamp");
			newComment.setValue(payload.data.count, "count");
			newComment.setLinkedRecords(
				payload.data.users.map(user => {
					let userRecord = store.get(user.id);
					if (userRecord) {
						return userRecord;
					}

					userRecord = store.create(user.id, "User");
					userRecord.setValue(user.id, "id");
					userRecord.setValue(user.email, "email");
					userRecord.setValue(user.fullName, "fullName");
					userRecord.setValue(user.image, "image");
					return userRecord;
				}),
				"users",
			);
			comments.push(newComment);
			meetingRecordingRecord.setLinkedRecords(comments, "comments");
		} else {
			meetingRecordingRecord.setLinkedRecords(updatedComments, "comments");
		}
	});
}
